import React, { RefObject, ReactNode, lazy, Suspense } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import FormattedTextInput from '../../FormattedTextInput';
const MentionAutocomplete = lazy(() => import('../MentionAutocomplete'));
import SendButton from './SendButton';
import type { FormattedTextInputHandle } from '../../FormattedTextInput';
import type { MentionAutocompleteHandle } from '../MentionAutocomplete';
import type { MentionFilters } from '../MentionTypes';

/**
 * Input container component
 *
 * Contains:
 * - FormattedTextInput for message input
 * - MentionAutocomplete for document/filter suggestions
 * - SendButton for sending/canceling messages
 * - Optional children (e.g., InputToolbar)
 */

export interface InputContainerProps {
  // Input state
  inputMessage: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onMentionClick: (mentionText: string, startIndex: number, endIndex: number) => void;
  onDocumentChipClick: (documentName: string) => void;

  // Mention autocomplete props
  onMentionSelect: (value: string) => void;
  onFiltersChange: (filters: MentionFilters) => void;
  onDocumentsAutoSelect: (documentIds: string[]) => void;
  contextFilters: MentionFilters;
  selectedDocumentIds: string[];
  conversationId?: string;

  // State
  isStreaming: boolean;
  canSend: boolean;

  // Callbacks
  onSend: () => void;
  onCancel: () => void;

  // Refs
  formattedInputRef: RefObject<FormattedTextInputHandle>;
  mentionAutocompleteRef: RefObject<MentionAutocompleteHandle>;

  // Children (e.g., InputToolbar)
  children?: ReactNode;
}

const InputContainer: React.FC<InputContainerProps> = ({
  inputMessage,
  onInputChange,
  onKeyDown,
  onMentionClick,
  onDocumentChipClick,
  onMentionSelect,
  onFiltersChange,
  onDocumentsAutoSelect,
  contextFilters,
  selectedDocumentIds,
  conversationId,
  isStreaming,
  canSend,
  onSend,
  onCancel,
  formattedInputRef,
  mentionAutocompleteRef,
  children,
}) => {
  const { theme } = useTheme();

  return (
    <div
      className={`${theme.colors.bg.input} border ${
        isStreaming ? 'border-nb-accent/30' : theme.colors.border.primary
      } rounded-2xl shadow-sm relative transition-colors duration-300`}
      style={{ overflow: 'visible', outline: 'none' }}
    >
      <div
        className="relative flex items-center"
        style={{ overflow: 'visible', outline: 'none', position: 'relative' }}
      >
        <FormattedTextInput
          ref={formattedInputRef}
          value={inputMessage}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onMentionClick={onMentionClick}
          onDocumentChipClick={onDocumentChipClick}
          activeMentionFilters={contextFilters}
          placeholder="¿Cómo puedo ayudarte hoy?"
          disabled={isStreaming}
          className={`flex-1 bg-transparent resize-none focus-visible:ring-2 focus-visible:ring-nb-accent/50 focus-visible:ring-offset-1 focus:outline-none disabled:opacity-50 ${theme.colors.text.primary}`}
          style={{
            fontFamily: 'inherit',
            paddingRight: '3rem',
          }}
        />
        <Suspense fallback={null}>
          <MentionAutocomplete
            ref={mentionAutocompleteRef}
            inputValue={inputMessage}
            onSelect={onMentionSelect}
            disabled={isStreaming}
            conversationId={conversationId}
            externalFilters={contextFilters}
            onFiltersChange={onFiltersChange}
            onDocumentsAutoSelect={onDocumentsAutoSelect}
            selectedDocumentIds={selectedDocumentIds}
          />
        </Suspense>

        {/* Send/Cancel button in input */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10">
          <SendButton
            isStreaming={isStreaming}
            canSend={canSend}
            onSend={onSend}
            onCancel={onCancel}
          />
        </div>
      </div>

      {/* Children (e.g., InputToolbar) */}
      {children}
    </div>
  );
};

export default InputContainer;
